import { useCallback, useRef, useState, type ReactNode } from 'react';
import { filterOptions, PopNotFound, PopSearch } from './Popover';
import { useDismiss } from './useDismiss';

/**
 * Ряд фильтров `.bar` (кит): фильтры слева, поиск справа за распоркой.
 * Подразделы и фильтры на одной строке не стоят (чек-лист, п. 19).
 */
export function FilterBar({ children, search }: { children?: ReactNode; search?: ReactNode }) {
  return (
    <div className="bar">
      {children}
      <div className="sp" />
      {search}
    </div>
  );
}

export interface FilterOption {
  value: string;
  label: string;
  /** Счётчик или подпись справа от пункта (кит 2.14: счётчики в списке фильтра) */
  side?: ReactNode;
}

/**
 * Фильтр `.ctl` + `.pop`. Закрытый читается как «Метка: значение»;
 * выбранный — ✕ сброса вместо шеврона (кит 2.8, 2.11). Поиск в списке —
 * всегда, сколько бы ни было вариантов (кит 2.15).
 */
export function FilterChip({
  label,
  value,
  options,
  onChange,
  alignRight,
}: {
  label: string;
  value: string | null;
  options: FilterOption[];
  onChange: (value: string | null) => void;
  alignRight?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrap = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, wrap);
  const current = options.find((o) => o.value === value);
  const visible = filterOptions(options, query);

  return (
    <div className="wrapctl" ref={wrap}>
      <div
        className={value ? 'ctl act' : 'ctl'}
        onClick={() => {
          setQuery('');
          setOpen((v) => !v);
        }}
      >
        {label}: {current?.label ?? 'все'}
        {value ? (
          <span
            className="x"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
              setOpen(false);
            }}
          >
            ✕
          </span>
        ) : (
          <svg>
            <use href="#chev" />
          </svg>
        )}
      </div>
      {open ? (
        <div className={alignRight ? 'pop rt' : 'pop'}>
          {options.length ? <PopSearch value={query} onChange={setQuery} /> : null}
          {!options.length ? (
            <div className="none">Вариантов нет</div>
          ) : visible.length ? (
            visible.map((o) => (
              <div
                key={o.value}
                className={o.value === value ? 'op on' : 'op'}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
                {o.side !== undefined ? <span className="sb">{o.side}</span> : null}
              </div>
            ))
          ) : (
            <PopNotFound />
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Фильтр с мультивыбором (доработка M2-1 п. 6): несколько значений
 * галочками `.ck`, поповер по клику на пункт не закрывается. Закрытый —
 * «Город: Томск, Иркутск», с третьего — «Город: Томск, Иркутск +1»
 * (multiShown в макете). ✕ сбрасывает все значения сразу.
 */
export function MultiFilterChip({
  label,
  values,
  options,
  onChange,
  alignRight,
}: {
  label: string;
  values: string[];
  options: FilterOption[];
  onChange: (values: string[]) => void;
  alignRight?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrap = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, wrap);
  const visible = filterOptions(options, query);
  const names = values.map((v) => options.find((o) => o.value === v)?.label ?? v);
  const shown = names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ');
  const toggle = (value: string) =>
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);

  return (
    <div className="wrapctl" ref={wrap}>
      <div
        className={values.length ? 'ctl act' : 'ctl'}
        onClick={() => {
          setQuery('');
          setOpen((v) => !v);
        }}
      >
        {label}: {shown || 'все'}
        {values.length ? (
          <span
            className="x"
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
              setOpen(false);
            }}
          >
            ✕
          </span>
        ) : (
          <svg>
            <use href="#chev" />
          </svg>
        )}
      </div>
      {open ? (
        <div className={alignRight ? 'pop rt' : 'pop'}>
          {options.length ? <PopSearch value={query} onChange={setQuery} /> : null}
          {!options.length ? (
            <div className="none">Вариантов нет</div>
          ) : visible.length ? (
            visible.map((o) => {
              const on = values.includes(o.value);
              return (
                // Без класса on: знак выбора здесь — флажок, полоса слева
                // была бы вторым знаком того же факта (кит, «Активное — линией»)
                <div key={o.value} className="op mop" onClick={() => toggle(o.value)}>
                  <span className={on ? 'ck on' : 'ck'} />
                  {o.label}
                  {o.side !== undefined ? <span className="sb">{o.side}</span> : null}
                </div>
              );
            })
          ) : (
            <PopNotFound />
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Поиск в ряду фильтров `.inp` — тонкий контур, фокус графитом (кит 2.9). */
export function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <input
      className="inp srchw"
      type="search"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
